const CACHE_PREFIX = "fb_cache_";
const DEFAULT_TTL = 60 * 1000; // 60 seconds — keep SWR snappy without serving stale config

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

export function getCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const entry: CacheEntry<T> = JSON.parse(raw);
    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

// Cópias de artigo são as entradas mais pesadas; quando o espaço acaba elas
// saem primeiro (as mais antigas antes), e a gravação é tentada de novo.
const EVICT_FIRST_PREFIX = CACHE_PREFIX + "blog_post_v1_";

function evictOldest(limit: number): number {
  const candidates: { key: string; expiresAt: number }[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(EVICT_FIRST_PREFIX)) continue;
    try {
      const entry = JSON.parse(localStorage.getItem(key) || "{}") as CacheEntry<unknown>;
      candidates.push({ key, expiresAt: entry.expiresAt || 0 });
    } catch {
      candidates.push({ key, expiresAt: 0 });
    }
  }
  candidates.sort((a, b) => a.expiresAt - b.expiresAt);
  const victims = candidates.slice(0, limit);
  victims.forEach((v) => localStorage.removeItem(v.key));
  return victims.length;
}

export function setCache<T>(key: string, data: T, ttlMs: number = DEFAULT_TTL): void {
  const entry: CacheEntry<T> = { data, expiresAt: Date.now() + ttlMs };
  let serialized: string;
  try {
    serialized = JSON.stringify(entry);
  } catch {
    return;
  }
  try {
    localStorage.setItem(CACHE_PREFIX + key, serialized);
  } catch {
    // Sem espaço: libera as cópias de artigo mais antigas e tenta uma vez mais.
    try {
      if (evictOldest(10) > 0) localStorage.setItem(CACHE_PREFIX + key, serialized);
    } catch {
      // localStorage indisponível — ignora em silêncio
    }
  }
}

/**
 * Removes cached entries. With no argument, clears the entire blog cache.
 * With a prefix, clears only matching keys (e.g. `blog_home_v2_`).
 */
export function invalidateCache(prefix?: string): void {
  try {
    const fullPrefix = CACHE_PREFIX + (prefix || "");
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(fullPrefix)) toRemove.push(key);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}
